import { OrderRuleStatus } from "@/features/line-sheet-sets/hooks/use-update-order-rule-status";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import useIdentity from "@/features/ui/hooks/use-identity";
import { Grid } from "@chakra-ui/react";
import { Formik } from "formik";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useLineSheetSetDumbTransaction } from "@/features/line-sheet-sets/hooks/use-line-sheet-set-dumb-transaction";
import useUpdateLineSheetSetV2, {
  UpdateLineSheetSetV2PathParameter,
  UpdateLineSheetSetV2Request,
} from "@/features/line-sheet-sets/hooks/use-update-line-sheet-set-v2";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { Agency, Brand, Company, StoredObject } from "@/features/types";
import {
  CommissionType,
  ConditionStatistics,
  DenseLineSheet,
  LightLineSheet,
  LineSheet,
  LineSheetSetStatus,
  LineSheetSetType,
  ReviewStatus,
} from "@/features/line-sheet-sets/line-sheet-set.types";
import useGetDenseLineSheetSet, {
  GetDenseLineSheetSetPathParameter,
  GetDenseLineSheetSetRequest,
} from "@/features/line-sheet-sets/hooks/use-get-dense-line-sheet-set";
import {
  DenseOrderSheet,
  LightOrderSheetSet,
} from "@/features/order-sheets/order-sheet.type";
import usePersistedLineSheetSetDetailPageQuery from "@/features/line-sheet-sets/detail/use-persisted-line-sheet-sheet-set-detail-page-query";
import { AppUtils } from "@/features/ui/utils/app-utils";
import LineSheetSetDetailPageHeader from "@/features/line-sheet-sets/detail/line-sheet-set-detail-page-header";
import LineSheetSetDetailForm from "@/features/line-sheet-sets/line-sheet-set-detail-form";
import useLineSheetSetFormValidationSchema from "@/features/line-sheet-sets/detail/hooks/use-line-sheet-set-form-validation-schema";
import useRequiredParams from "@/features/invoices/hooks/use-required-params";
import usePublishLineSheetSetIfConfirmed from "@/features/line-sheet-sets/hooks/use-publish-line-sheet-set-if-confirmed";
import useCreateOrderSheetSetInfinite from "@/features/line-sheet-sets/hooks/use-create-order-sheet-set-infinite";
import CenteredSpinner from "@/shared/components/centered-spinner";
import LineSheetNotFoundPage from "@/features/line-sheet-sets/line-sheet-not-found-page";
import { isLineSheetSetUnavailableError } from "@/features/line-sheet-sets/line-sheet-set-error";

interface LineSheetSetDetailPageProps {
  isEditing?: boolean;
}

export type LineSheetFormField = Omit<
  DenseLineSheet,
  "file" | "lineSheetTabs" | "status"
> & {
  file: Pick<StoredObject, "id" | "name"> | File;
  unoFormFile?: Pick<StoredObject, "id" | "name"> | File | null;
  status?: LineSheet["status"];
  brands?: LineSheet["brands"];
  seasons?: LineSheet["seasons"];
  isChecked: boolean;
  isCheckable: boolean;
  isOrderRule?: boolean;
  source?: LineSheetFormField | AttachmentFormField;
  isAllProductsHaveBrand?: LineSheet["isAllProductsHaveBrand"];
};

export type AttachmentFormField = Pick<StoredObject, "id" | "name"> & {
  file?: File;
  isChecked: boolean;
  isCheckable: boolean;
  isOrderRule?: boolean;
  // prevLineSheetFormFields?: LineSheetFormField;
  source?: LineSheetFormField | AttachmentFormField;
};

export type OrderSheetFormField = Omit<
  DenseOrderSheet,
  "orderSheetTabs" | "lineSheet"
> & {
  isChecked: boolean;
  isCheckable: boolean;
  isOrderRule?: boolean;
  lineSheet: Omit<LightLineSheet, "lineSheetTabs">;
};

export function isLineSheetFormField(
  value: LineSheetFormField | AttachmentFormField | OrderSheetFormField
): value is LineSheetFormField {
  return (
    (value as LineSheetFormField).type !== undefined &&
    (value as OrderSheetFormField).lineSheet === undefined
  );
}

export function isAttachmentFormField(
  value: LineSheetFormField | AttachmentFormField | OrderSheetFormField
): value is AttachmentFormField {
  return !isOrderSheetFormField(value) && !isLineSheetFormField(value);
}

export function isOrderSheetFormField(
  value: LineSheetFormField | AttachmentFormField | OrderSheetFormField
): value is OrderSheetFormField {
  return (value as OrderSheetFormField).lineSheet !== undefined;
}

export interface LineSheetSetFormFields {
  id?: number;
  name?: string;
  season?: string;
  issuedAt?: Date;
  issuedByBoutique?: {
    id: number;
    name: string;
    orgName?: string | null;
  };
  brands?: Brand[] | null;
  status?: LineSheetSetStatus;
  reviewStatus?: ReviewStatus | null;
  aiImageStatus?: string | null;
  aiImageCompletedAt?: string | null;
  applyConditionCompletedAt?: string | null;
  orderRuleStatus?: OrderRuleStatus;
  conditionStatistics?: ConditionStatistics | null;
  type?: LineSheetSetType;
  commission?: number | null;
  commissionType?: CommissionType | null;
  applyIva?: boolean | null;
  moq?: number | null;
  currency?: string;
  validUntil?: Date;
  joinedOrderRules?: string;
  //수정시에 순서 보장을 위해서 lineSheet와 attachment를 합쳐야함,
  lineSheetOrAttachmentList: (
    | LineSheetFormField
    | AttachmentFormField
    | OrderSheetFormField
  )[];
  createdByCompany?: Agency;
  createdAt?: Date;
  publishedAt?: Date | null;
  externalFileLinks: string[];
  compressionStatus?: LineSheet["compressionStatus"];
  compressedImportedFile?: StoredObject | null;
  compressedUnoFormFile?: StoredObject | null;
  unoFormFile?: StoredObject | null;
  orderSheetSet?: LightOrderSheetSet;
  isDeleted: boolean;
  issuedToBuyers?: { id: number; name: string }[];
}

export default function LineSheetSetDetailPage({
  isEditing = false,
}: LineSheetSetDetailPageProps) {
  const identity = useIdentity();
  const { value: query } = usePersistedLineSheetSetDetailPageQuery();
  const { lineSheetSetId } = useRequiredParams<{ lineSheetSetId: number }>();
  const { navigate, navigateToBuyerOrderSheetSetPage } = useRouterHelper();
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const { error: showError } = useAppToasts();

  const getDenseLineSheetSetKey = useMemo(():
    | {
        parameter: GetDenseLineSheetSetPathParameter;
        request: GetDenseLineSheetSetRequest;
      }
    | undefined => {
    if (identity?.company && lineSheetSetId) {
      if (lineSheetSetId) {
        return {
          parameter: {
            lineSheetSetId,
            by: identity.company.type,
            companyId: identity.company.id,
            orderSheetSetId: query.orderSheetSetId,
            infer: identity.company.type === "BUYER",
          },
          request: {
            query: query.query,
            "madeByCompany.id__in": query["madeByCompany.id__in"],
            season__in: query["season__in"],
          },
        };
      }
    }
    return undefined;
  }, [identity, lineSheetSetId, query]);

  const {
    data: getDenseLineSheetSetData,
    mutate: mutateGetLineSheetSetData,
    error: getLineSheetSetError,
  } = useGetDenseLineSheetSet(
    getDenseLineSheetSetKey?.parameter,
    getDenseLineSheetSetKey?.request,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      // 존재하지 않는/접근 불가 라인시트는 아래에서 안내 페이지로 렌더하므로 전역 에러 토스트를 띄우지 않는다.
      // (그 외 에러는 전역 SWRConfig.onError 와 동일하게 토스트로 알린다)
      onError: (err) => {
        if (!isLineSheetSetUnavailableError(err)) {
          showError(err);
        }
      },
    }
  );

  const { fire: fireCreateOrderSheetSet, isLoading: isCreateOssLoading } =
    useCreateOrderSheetSetInfinite();

  const isParsingSkipped =
    getDenseLineSheetSetData?.status === "PARSING_SKIPPED";

  useEffect(() => {
    if (
      !getDenseLineSheetSetData ||
      identity?.company?.type === "AGENCY" ||
      // 주문(OSS)을 만드는 것은 바이어뿐이다. 부티크는 진입 경로와 무관하게
      // 에이전시처럼 이 라인시트세트 상세(OSS 인라인)에 머문다.
      // 부티크로 부르면 POST /boutiques/{id}/order-sheet-sets 라 라우트 자체가 없어
      // 405 로 떨어진다 — 성공할 수 없는 호출이었다.
      identity?.company?.type === "BOUTIQUE" ||
      isCreateOssLoading ||
      isParsingSkipped
    )
      return;

    const orderSheetSetId = getDenseLineSheetSetData.orderSheetSet?.id;
    const lineSheetSetId = getDenseLineSheetSetData.id;

    const navigateHelper = (ossId: number, lssId: number) =>
      navigateToBuyerOrderSheetSetPage(
        ossId,
        lssId,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
        false
      );

    if (orderSheetSetId && orderSheetSetId > 0) {
      navigateHelper(orderSheetSetId, lineSheetSetId);
    } else {
      if (identity?.company) {
        fireCreateOrderSheetSet(
          {
            by: identity.company.type,
            companyId: identity.company.id,
          },
          {
            lineSheetSet: {
              id: lineSheetSetId,
            },
          }
        ).then((data) => {
          if (data?.id) {
            navigateHelper(data.id, lineSheetSetId);
          }
        });
      }
    }
  }, [
    isParsingSkipped,
    isCreateOssLoading,
    getDenseLineSheetSetData,
    navigateToBuyerOrderSheetSetPage,
    fireCreateOrderSheetSet,
    identity,
  ]);

  const initialValues = useMemo((): LineSheetSetFormFields => {
    if (getDenseLineSheetSetData) {
      // const ignoreEmptyLineSheet =
      //   getDenseLineSheetSetData.orderSheetSet !== undefined &&
      //   isOrderedToAgency(getDenseLineSheetSetData.orderSheetSet);
      const {
        orderRules,
        lineSheets,
        attachments,
        publishedAt,
        isDeleted,
        orderRuleFiles,
        ...rest
      } = getDenseLineSheetSetData;

      const isPublished = publishedAt !== null;
      const isAgency = identity?.company?.type === "AGENCY";
      const isBoutique = identity?.company?.type === "BOUTIQUE";
      const isLineSheetSetOrAttachmentCheckable = !isPublished && isAgency;

      const lineSheetOrAttachmentList: LineSheetSetFormFields["lineSheetOrAttachmentList"] =
        [
          ...lineSheets.flatMap(
            (lineSheet): (LineSheetFormField | OrderSheetFormField)[] => {
              if (lineSheet.orderSheets.length > 0) {
                return lineSheet.orderSheets.map((orderSheet) => {
                  let isOrderSheetCheckable;
                  if (isAgency || isBoutique) {
                    isOrderSheetCheckable = ![
                      "INITIAL_DRAFT",
                      "CANCELED",
                      // "MODIFICATION_REQUIRED",
                      // "MODIFICATION_COMPLETED",
                      "COMPLETED",
                    ].includes(orderSheet.status);
                  } else {
                    //파싱실패거나 오더폼일 경우 수량이 0이어도 체크 가능
                    const isEffectivelyOrderForm =
                      lineSheet.type === "ORDER_FORM" ||
                      (lineSheet.type === "LINE_SHEET" &&
                        lineSheet.status === "PARSING_FAILED");

                    isOrderSheetCheckable =
                      (isEffectivelyOrderForm ||
                        orderSheet.orderedQuantity > 0) &&
                      [
                        "WORKING_DRAFT",
                        "ORDERED_TO_AGENCY",
                        "RESUBMIT_REQUIRED",
                      ].includes(orderSheet.status);
                  }

                  return {
                    ...orderSheet,
                    lineSheet: lineSheet,
                    isChecked: false,
                    isCheckable: isOrderSheetCheckable,
                  };
                });
              } else {
                return [
                  {
                    ...lineSheet,
                    file: lineSheet.file,
                    unoFormFile: lineSheet.unoFormFile,
                    isChecked: false,
                    isCheckable: isLineSheetSetOrAttachmentCheckable,
                  },
                ];
              }
            }
          ),
          ...attachments.map((attachment) => {
            return {
              ...attachment,
              isChecked: false,
              isCheckable: isLineSheetSetOrAttachmentCheckable,
            };
          }),
          ...orderRuleFiles.map((orderRuleFile) => {
            return {
              ...orderRuleFile,
              isChecked: false,
              isCheckable: isLineSheetSetOrAttachmentCheckable,
              isOrderRule: true,
            };
          }),
        ];

      if (getDenseLineSheetSetData.orderSheetSet) {
        const getOrder = (
          field: LineSheetFormField | OrderSheetFormField | AttachmentFormField
        ) => {
          if (isOrderSheetFormField(field)) {
            if (field.orderedQuantity > 0) {
              return 1;
            } else {
              return 2;
            }
          } else if (isLineSheetFormField(field)) {
            return 2;
          } else {
            return 3;
          }
        };
        lineSheetOrAttachmentList.sort((a, b) => {
          return getOrder(a) - getOrder(b);
        });
      }

      return {
        ...rest,
        lineSheetOrAttachmentList,
        joinedOrderRules: orderRules.join("\n"),
        publishedAt: publishedAt,
        isDeleted: isDeleted,
      };
    } else {
      return {
        externalFileLinks: [],
        lineSheetOrAttachmentList: [],
        isDeleted: false,
        commissionType: "ORDER",
      };
    }
  }, [getDenseLineSheetSetData, identity]);

  const validateSchema = useLineSheetSetFormValidationSchema();

  async function validate(values: LineSheetSetFormFields) {
    try {
      await validateSchema.validate(values, { abortEarly: false });
    } catch (e: any) {
      showError(e);
      return e;
    }
  }

  const { addUpdateLineSheetSetTransaction } = useLineSheetSetDumbTransaction();

  const {
    fire: fireUpdateLineSheetSet,
    data: updateLineSheetSetData,
    error: updateLineSheetSetError,
    isLoading: isUpdateLineSheetSetLoading,
    clear: clearUpdateLineSheetSet,
  } = useUpdateLineSheetSetV2();

  async function handleSubmit(values: LineSheetSetFormFields) {
    const companyType = identity?.company?.type;
    if (
      identity?.company &&
      (companyType === "AGENCY" || companyType === "BOUTIQUE")
    ) {
      // 부티크도 자기 라인시트세트를 수정한다 — 발행 부티크는 자기로 고정되고 수수료는 서버가 무시한다
      const parameter: UpdateLineSheetSetV2PathParameter =
        companyType === "BOUTIQUE"
          ? { boutiqueId: identity.company.id, lineSheetSetId: values.id! }
          : { agencyId: identity.company.id, lineSheetSetId: values.id! };

      const request: Omit<
        UpdateLineSheetSetV2Request,
        "lineSheets" | "attachments" | "orderRuleFiles"
      > = {
        name: values.name!,
        season: values.season!,
        type: values.type!,
        issuedByBoutique: {
          id:
            companyType === "BOUTIQUE"
              ? identity.company.id
              : values.issuedByBoutique!.id,
        },
        commission: values.commission,
        commissionType: values.commissionType,
        applyIva: values.applyIva,
        moq: values.moq,
        currency: values.currency!,
        orderRules: values.joinedOrderRules!.split("\n"),
        validUntil: values.validUntil!,
        externalFileLinks: values.externalFileLinks || [],
      };

      if (
        values.lineSheetOrAttachmentList
          .filter(
            (i): i is LineSheetFormField | AttachmentFormField =>
              isLineSheetFormField(i) || isAttachmentFormField(i)
          )
          .some((lineSheetOrAttachment) => {
            if (lineSheetOrAttachment.file instanceof File) {
              return true;
            }
          })
      ) {
        addUpdateLineSheetSetTransaction(parameter, {
          ...request,
          lineSheets: values.lineSheetOrAttachmentList
            .filter(isLineSheetFormField)
            .map((lineSheet) => {
              return {
                id: lineSheet.id,
                type: lineSheet.type,
                name: lineSheet.name,
                file: lineSheet.file,
                unoFormFile: lineSheet.unoFormFile,
                reviewStatus: lineSheet.reviewStatus,
              };
            }),
          attachments: values.lineSheetOrAttachmentList
            .filter(isAttachmentFormField)
            .filter((i) => !i.isOrderRule)
            .map((attachment) => {
              if (attachment.file) {
                return attachment.file;
              } else {
                return {
                  id: attachment.id,
                  name: attachment.name,
                };
              }
            }),
          orderRuleFiles: values.lineSheetOrAttachmentList
            .filter((i) => i.isOrderRule)
            .filter(isAttachmentFormField)
            .map((item) => {
              if (item.file) {
                return item.file;
              } else {
                return {
                  id: item.id,
                  name: item.name,
                };
              }
            }),
        });
      } else {
        const response = await fireUpdateLineSheetSet(parameter, {
          ...request,
          lineSheets: values.lineSheetOrAttachmentList
            .filter(isLineSheetFormField)
            .map((lineSheet) => {
              return {
                id: lineSheet.id,
                type: lineSheet.type,
                name: lineSheet.name,
                file: lineSheet.file as Pick<StoredObject, "id">,
                reviewStatus: lineSheet.reviewStatus,
                brands: lineSheet.brands,
              };
            }),
          attachments: values.lineSheetOrAttachmentList
            .filter(isAttachmentFormField)
            .filter((i) => !i.isOrderRule)
            .map((attachment) => {
              return {
                id: attachment.id,
                name: attachment.name,
              };
            }),
          orderRuleFiles: values.lineSheetOrAttachmentList
            .filter((i) => i.isOrderRule)
            .filter(isAttachmentFormField)
            .map((item) => {
              return {
                id: item.id,
                name: item.name,
              };
            }),
        });

        await mutateGetLineSheetSetData();
        return response;
      }
    }

    await AppUtils.delay(500);

    return true;
  }

  useEffect(() => {
    async function refresh() {
      if (!isEditing) {
        setIsRefreshing(true);
        await mutateGetLineSheetSetData();
        setIsRefreshing(false);
      }
    }

    let intervalId: NodeJS.Timer | null = null;
    if (getDenseLineSheetSetData && !isEditing) {
      if (
        getDenseLineSheetSetData.lineSheets.some(
          (lineSheet) =>
            lineSheet.status === "PARSING_PENDING" ||
            lineSheet.compressionStatus === "PENDING"
        )
      ) {
        intervalId = setInterval(refresh, 5 * 1000);
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [getDenseLineSheetSetData, isEditing, mutateGetLineSheetSetData]);

  const { fire: publishLineSheetSetIfConfirmed } =
    usePublishLineSheetSetIfConfirmed();

  const handlePublishLineSheetSetClick = useCallback(async () => {
    if (lineSheetSetId) {
      const result = await publishLineSheetSetIfConfirmed([lineSheetSetId]);
      if (result) {
        mutateGetLineSheetSetData();
      }
    }
  }, [
    lineSheetSetId,
    publishLineSheetSetIfConfirmed,
    mutateGetLineSheetSetData,
  ]);

  // 삭제/미발행 등으로 조회가 실패(404/403)하면 빈 화면+토스트 대신 안내 페이지를 보여준다.
  if (
    getLineSheetSetError &&
    isLineSheetSetUnavailableError(getLineSheetSetError)
  ) {
    return <LineSheetNotFoundPage />;
  }

  if (identity?.company?.type === "BUYER" && !isParsingSkipped) {
    return <CenteredSpinner />;
  }

  return (
    <Formik<LineSheetSetFormFields>
      initialValues={initialValues}
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      enableReinitialize={true}
      onSubmit={async (values, formikHelpers) => {
        try {
          formikHelpers.setSubmitting(true);
          const response = await handleSubmit(values);

          if (response) {
            const isByDumb = typeof response === "boolean";

            if (!isByDumb) {
              clearUpdateLineSheetSet();
            }

            navigate(-1, {
              replace: true,
              defaultPage: `/line-sheet-sets/${lineSheetSetId}`,
            });
          }
        } finally {
          formikHelpers.setSubmitting(false);
        }
      }}
    >
      {(props) => {
        return (
          <Grid
            as={"form"}
            height={"100%"}
            width={"100%"}
            gridTemplateColumns={"minmax(0, 1fr)"}
            gridTemplateRows={"auto auto 1fr"}
          >
            <LineSheetSetDetailPageHeader
              initialQuery={query}
              formik={props}
              isEditing={isEditing}
              mutate={mutateGetLineSheetSetData}
              onPublishClick={handlePublishLineSheetSetClick}
            />
            <LineSheetSetDetailForm
              formik={props}
              isEditing={isEditing}
              mutate={mutateGetLineSheetSetData}
              isRefreshing={isRefreshing}
              onPublishClick={handlePublishLineSheetSetClick}
            />
          </Grid>
        );
      }}
    </Formik>
  );
}
